import { Link } from "react-router-dom";
import { Stethoscope, Phone, MapPin, ArrowRight } from "lucide-react";
import PageWrapper from "@/components/layout/PageWrapper";
import Breadcrumb from "@/components/Breadcrumb";
import StarRating from "@/components/StarRating";
import ReviewsSlider from "@/components/ReviewsSlider";
import Icon from "@/components/Icon";
import { useLanguage } from "@/i18n/LanguageContext";
import { CONTACT, IMAGES, PHYSIO_SERVICES, PHYSIO_SPECIALIZED, PATIENT_REVIEWS } from "@/data/content";

const Physiotherapy = () => {
  const { t } = useLanguage();
  return (
    <PageWrapper
      title="Bhadrak Physiotherapy & Rehab Centre — Care, Therapy & Rehabilitation"
      description="Bhadrak Physiotherapy & Rehab Centre offers physical therapy, neurological rehab, pediatric, geriatric and post-surgery rehabilitation in Bhadrak, Odisha."
    >
      {/* HERO */}
      <section
        data-testid="physio-hero"
        className="relative overflow-hidden"
        style={{ background: "#0f1e26", color: "#e8eef0" }}
      >
        <div className="adhd-container relative grid lg:grid-cols-12 gap-10 py-16 lg:py-24">
          <div className="lg:col-span-7 flex flex-col gap-7">
            <Breadcrumb pageKey="nav.physio" testId="physio-breadcrumb" />
            <img
              src="/physio-logo.png"
              alt="Bhadrak Physiotherapy & Occupational Therapy Rehab Center logo"
              data-testid="physio-logo"
              decoding="async"
              className="h-24 w-24 sm:h-28 sm:w-28 object-contain mb-1"
            />
            <p className="text-[11px] uppercase tracking-[0.22em] font-semibold" style={{ color: "var(--adhd-accent)" }}>
              {t("physio.eyebrow")}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-tight text-white">
              {t("physio.title")}
            </h1>
            <p className="text-lg opacity-85 max-w-2xl">{t("physio.tagline")}</p>

            <div className="flex flex-wrap gap-3 mt-2">
              <a
                href={`tel:${CONTACT.phoneTel}`}
                data-testid="physio-call"
                className="adhd-btn-accent"
              >
                <Phone size={14} /> {t("cta.call")}
              </a>
              <Link to="/doctor" data-testid="physio-meet-doctor" className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-semibold rounded-full border border-white/20 hover:bg-white/10 transition">
                <Stethoscope size={14} /> {t("cta.doctor")}
              </Link>
              <a
                href={CONTACT.mapShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3.5 text-sm font-semibold rounded-full text-white hover:bg-white/10 transition"
                data-testid="physio-map"
              >
                <MapPin size={14} /> {t("cta.location")}
              </a>
            </div>

            <div className="flex items-center gap-4 mt-3">
              <StarRating rating={CONTACT.rating} size={16} testId="physio-rating" />
              <p className="text-sm">
                <span className="font-semibold text-white">{CONTACT.rating} Rating</span>
                <span className="opacity-70"> · {CONTACT.reviewCount}+ patient reviews</span>
              </p>
            </div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="aspect-[4/5] rounded-[2rem] overflow-hidden">
              <img
                src="/physio-hero.jpeg"
                alt="Bhadrak Physiotherapy & Rehab Centre"
                decoding="async"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section data-testid="physio-services" className="adhd-section">
        <div className="adhd-container">
          <p className="adhd-eyebrow"><Stethoscope size={13} /> Treatments</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-[color:var(--adhd-text)]">
            {t("physio.servicesTitle")}
          </h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {PHYSIO_SERVICES.map((s, i) => (
              <article
                key={`${s.title}-${i}`}
                data-testid={`physio-service-${i}`}
                className="adhd-card p-5 flex flex-col gap-4 min-h-[150px]"
              >
                <span
                  className="grid place-items-center h-10 w-10 rounded-xl"
                  style={{ background: "rgba(58,124,165,0.10)", color: "var(--adhd-primary)" }}
                >
                  <Icon name={s.icon} size={18} />
                </span>
                <p className="font-semibold text-[15px] text-[color:var(--adhd-text)] leading-snug">
                  {s.title}
                </p>
              </article>
            ))}
          </div>

          {/* Specialized treatments & rehabilitation programs */}
          <h3
            data-testid="physio-specialized-title"
            className="mt-14 font-serif text-2xl sm:text-3xl text-[color:var(--adhd-text)]"
          >
            Specialized Treatments & Rehabilitation Programs
          </h3>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
            {PHYSIO_SPECIALIZED.map((s, i) => (
              <article
                key={`${s.title}-${i}`}
                data-testid={`physio-specialized-${i}`}
                className="adhd-card p-6 flex flex-col gap-4"
              >
                <span
                  className="grid place-items-center h-11 w-11 rounded-xl"
                  style={{ background: "rgba(58,124,165,0.10)", color: "var(--adhd-primary)" }}
                >
                  <Icon name={s.icon} size={20} />
                </span>
                <p className="font-semibold text-[16px] text-[color:var(--adhd-text)] leading-snug">
                  {s.title}
                </p>
                <p className="text-sm leading-relaxed text-[color:var(--adhd-muted)]">
                  {s.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* PATIENT REVIEWS */}
      <section
        data-testid="physio-reviews-section"
        className="adhd-section"
        style={{ background: "var(--adhd-bg-soft)" }}
      >
        <div className="adhd-container">
          <p className="adhd-eyebrow">⭐ Patient Voices</p>
          <h2 className="mt-3 font-serif text-3xl sm:text-4xl text-[color:var(--adhd-text)]">
            {t("physio.reviewsTitle")}
          </h2>
        </div>
        <div className="mt-10">
          <ReviewsSlider reviews={PATIENT_REVIEWS} testId="physio-reviews-slider" />
        </div>
      </section>

      {/* CTA */}
      <section className="adhd-container mb-10">
        <div className="adhd-card p-8 sm:p-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="max-w-xl">
            <h3 className="font-serif text-2xl sm:text-3xl text-[color:var(--adhd-text)]">
              Book a consultation with Dr. Manas Ranjan Jena
            </h3>
            <p className="mt-2 text-sm text-[color:var(--adhd-muted)]">
              Patient-first care and holistic rehabilitation in the heart of Bhadrak.
            </p>
          </div>
          <Link to="/contact" data-testid="physio-cta-book" className="adhd-btn-primary">
            Book Consultation <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </PageWrapper>
  );
};

export default Physiotherapy;
